export const CATEGORY_COLORS = {
    performance: '#D4A017',
    recovery:    '#A855F7',
    longevity:   '#2DD4BF',
    wellness:    '#22C55E',
};

export const CATEGORY_DEFINITIONS = {
    performance: {
        display_name: 'Performance',
        icon: 'trophy',
        description: 'Optimize athletic output, body composition, and physical capacity.',
        color: CATEGORY_COLORS.performance,
        focuses: {
            muscle_gain: {
                display_name: 'Muscle Gain',
                icon: 'dumbbell',
                description: 'Hypertrophy, strength, and anabolic optimization.',
                assessment_ids: ['hormonal_male', 'hormonal_female', 'musculoskeletal', 'metabolic_syndrome', 'cardiovascular'],
            },
            fat_loss: {
                display_name: 'Fat Loss',
                icon: 'flame',
                description: 'Metabolic rate, insulin sensitivity, and sustainable deficit.',
                assessment_ids: ['metabolic_syndrome', 'thyroid_function', 'adrenal_hpa', 'cardiovascular', 'gastrointestinal'],
            },
            endurance: {
                display_name: 'Endurance',
                icon: 'running',
                description: 'Oxygen transport, aerobic capacity, and recovery.',
                assessment_ids: ['cardiovascular', 'respiratory', 'musculoskeletal', 'adrenal_hpa'],
            },
            general_fitness: {
                display_name: 'General Fitness',
                icon: 'activity',
                description: 'Overall physical health and exercise readiness.',
                assessment_ids: ['cardiovascular', 'musculoskeletal', 'metabolic_syndrome', 'sleep_circadian'],
            },
        },
    },
    recovery: {
        display_name: 'Recovery & Chronic Health',
        icon: 'heart-pulse',
        description: 'Investigate symptoms, track chronic conditions, and guide recovery.',
        color: CATEGORY_COLORS.recovery,
        focuses: {
            chronic_fatigue: {
                display_name: 'Chronic Fatigue',
                icon: 'battery-low',
                description: 'Persistent exhaustion, post-exertional malaise, and energy recovery.',
                assessment_ids: ['brain_function', 'brain_health_nutrition', 'adrenal_hpa', 'thyroid_function', 'sleep_circadian', 'immune_system'],
            },
            autoimmune: {
                display_name: 'Autoimmune',
                icon: 'shield-alert',
                description: 'Autoimmune markers, flare tracking, and inflammation management.',
                assessment_ids: ['brain_function', 'brain_health_nutrition', 'immune_system', 'gastrointestinal', 'oxidative_stress'],
            },
            post_illness: {
                display_name: 'Post-Illness Recovery',
                icon: 'trending-up',
                description: 'Recovering from acute illness, surgery, or infection.',
                assessment_ids: ['immune_system', 'respiratory', 'gastrointestinal', 'adrenal_hpa', 'oxidative_stress'],
            },
            general_recovery: {
                display_name: 'General Recovery',
                icon: 'heart',
                description: 'Overall symptom investigation and health restoration.',
                assessment_ids: ['adrenal_hpa', 'thyroid_function', 'immune_system', 'sleep_circadian', 'gastrointestinal'],
            },
        },
    },
    longevity: {
        display_name: 'Longevity & Prevention',
        icon: 'hourglass',
        description: 'Minimize disease risk, slow aging, and optimize healthspan.',
        color: CATEGORY_COLORS.longevity,
        focuses: {
            metabolic_health: {
                display_name: 'Metabolic Health',
                icon: 'chart-line',
                description: 'Insulin sensitivity, blood sugar stability, and metabolic syndrome prevention.',
                assessment_ids: ['metabolic_syndrome', 'cardiovascular', 'liver_detoxification', 'gastrointestinal'],
            },
            cardiovascular: {
                display_name: 'Cardiovascular Prevention',
                icon: 'heart-shield',
                description: 'Heart disease risk reduction and vascular health.',
                assessment_ids: ['cardiovascular', 'metabolic_syndrome', 'oxidative_stress', 'adrenal_hpa'],
            },
            cognitive: {
                display_name: 'Cognitive Preservation',
                icon: 'brain',
                description: 'Brain health, neuroprotection, and cognitive decline prevention.',
                assessment_ids: ['brain_function', 'brain_health_nutrition', 'sleep_circadian', 'oxidative_stress', 'autonomic_nervous'],
            },
        },
    },
    wellness: {
        display_name: 'General Wellness',
        icon: 'leaf',
        description: 'Understand your bloodwork and maintain good health.',
        color: CATEGORY_COLORS.wellness,
        focuses: {
            nutritional_gaps: {
                display_name: 'Nutritional Gaps',
                icon: 'apple',
                description: 'Identify and address vitamin and mineral deficiencies.',
                assessment_ids: ['gastrointestinal', 'skin_hair_nails', 'aging_cellular', 'oxidative_stress'],
            },
            hormonal_balance: {
                display_name: 'Hormonal Balance',
                icon: 'balance',
                description: 'Hormone levels, thyroid health, and endocrine function.',
                assessment_ids: ['hormonal_male', 'hormonal_female', 'thyroid_function', 'adrenal_hpa', 'autonomic_nervous'],
            },
            general_checkup: {
                display_name: 'General Check-up',
                icon: 'clipboard',
                description: 'Overall health status and basic bloodwork interpretation.',
                assessment_ids: ['cardiovascular', 'metabolic_syndrome', 'thyroid_function', 'sleep_circadian'],
            },
        },
    },
};

export const FOCUS_LOOKUP = {};
for (const [categoryId, category] of Object.entries(CATEGORY_DEFINITIONS)) {
    for (const [focusId, focus] of Object.entries(category.focuses)) {
        FOCUS_LOOKUP[focusId] = {
            ...focus,
            categoryId,
            color: category.color,
        };
    }
}

export const VALID_FOCUS_IDS = Object.keys(FOCUS_LOOKUP);

export const ASSESSMENT_SHORT_LABELS = {
    hormonal_male: 'Hormonal',
    hormonal_female: 'Hormonal',
    musculoskeletal: 'MSK',
    metabolic_syndrome: 'Metabolic',
    cardiovascular: 'Cardio',
    gastrointestinal: 'Gastro',
    thyroid_function: 'Thyroid',
    adrenal_hpa: 'Adrenal',
    sleep_circadian: 'Sleep',
    immune_system: 'Immune',
    respiratory: 'Respiratory',
    brain_function: 'Brain',
    brain_health_nutrition: 'Brain Nutr',
    oxidative_stress: 'Oxidative',
    autonomic_nervous: 'Autonomic',
    liver_detoxification: 'Liver',
    skin_hair_nails: 'Skin/Hair',
    aging_cellular: 'Aging',
};

export const ASSESSMENT_DESCRIPTIONS = {
    hormonal_male: 'Evaluates testosterone, DHEA, estradiol, and other androgens that drive muscle growth, energy, libido, and metabolic rate in men.',
    hormonal_female: 'Evaluates estrogen, progesterone, DHEA, and other hormones that regulate menstrual health, bone density, mood, and metabolic balance in women.',
    musculoskeletal: 'Assesses markers related to bone mineral density, joint inflammation, muscle recovery, and connective tissue health.',
    metabolic_syndrome: 'Screens for insulin resistance, blood sugar dysregulation, dyslipidemia, and visceral adiposity — the cluster of risk factors behind type 2 diabetes and cardiovascular disease.',
    cardiovascular: 'Evaluates heart disease risk through lipid panels, inflammatory markers, blood pressure indicators, and vascular function.',
    gastrointestinal: 'Assesses gut permeability, digestive enzyme function, microbial balance, and nutrient absorption capacity.',
    thyroid_function: 'Measures TSH, free T3/T4, and thyroid antibodies to evaluate metabolic regulation, energy production, and thermoregulation.',
    adrenal_hpa: 'Evaluates cortisol rhythm, DHEA-S, and HPA axis function — the stress-response system that affects energy, sleep, immunity, and recovery.',
    sleep_circadian: 'Assesses sleep architecture, circadian rhythm alignment, melatonin-related markers, and their impact on recovery and cognitive function.',
    immune_system: 'Evaluates white blood cell differentials, inflammatory cytokines, and immune resilience indicators.',
    respiratory: 'Assesses lung function markers, oxygen transport efficiency, and respiratory inflammation.',
    brain_function: 'Evaluates neurotransmitter-related markers, cognitive performance indicators, and neuroinflammation.',
    brain_health_nutrition: 'Assesses nutritional cofactors critical for brain health — B vitamins, omega-3 status, magnesium, and antioxidant levels.',
    oxidative_stress: 'Measures free radical damage, antioxidant capacity, and cellular aging markers like glutathione and lipid peroxidation.',
    autonomic_nervous: 'Evaluates sympathetic/parasympathetic balance through heart rate variability markers and stress-response indicators.',
    liver_detoxification: 'Assesses liver enzyme levels, phase I/II detoxification capacity, and toxin clearance efficiency.',
    skin_hair_nails: 'Evaluates nutritional and hormonal markers affecting skin elasticity, hair growth, and nail strength.',
    aging_cellular: 'Assesses biomarkers of cellular senescence, telomere-related indicators, and age-acceleration factors.',
};

const SEX_SPECIFIC = {
    hormonal_male: 'male',
    hormonal_female: 'female',
};

export function filterAssessmentsBySex(assessmentIds, sex) {
    if (!sex) return assessmentIds;
    const opposite = sex === 'male' ? 'female' : 'male';
    return assessmentIds.filter(id => SEX_SPECIFIC[id] !== opposite);
}
